/**
 * Completed Strategies Table Component
 *
 * Displays a table of completed (historical) strategies with their P&L summary.
 * Used in the "History" tab of the trader dashboard.
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  Tooltip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  Popover,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildIcon from '@mui/icons-material/Build';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { useTraderData } from '../../contexts/TraderDataContext';
import type { CompletedStrategyListItemV2 } from '../../types/strategy';
import { getStateColor } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';
import { StrategyDetailDialog } from './StrategyDetailDialog';
import { getExchangeLogo, EXCHANGE_NAMES } from '../../utils/exchangeUrls';
import { exportCompletedStrategies } from '../../utils/strategyExport';

/**
 * Convert exchange name string to index for logo lookup
 */
function getExchangeIndex(exchangeName: string | undefined | null): number | undefined {
  if (!exchangeName) return undefined;
  const normalized = exchangeName.toLowerCase().replace('.', '').replace('-', '');
  const index = EXCHANGE_NAMES.findIndex(name => name === normalized);
  return index >= 0 ? index : undefined;
}

/**
 * Sort field options for completed strategies
 */
type CompletedSortField = 'completedAt' | 'pnl' | 'pnlPercent' | 'base' | 'duration';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: CompletedSortField;
  direction: SortDirection;
}

/**
 * Format date for display
 */
function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleDateString('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format duration between two timestamps
 */
function formatDuration(startTime: number, endTime: number): string {
  if (!startTime || !endTime) return '-';
  const durationMs = endTime - startTime;
  if (durationMs < 0) return '-';

  const totalSeconds = Math.floor(durationMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Format profit with sign
 */
function formatProfit(profit: number): string {
  const sign = profit >= 0 ? '+' : '';
  return `${sign}$${profit.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format percentage with sign
 */
function formatPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Format profit tiers for display (e.g., "3.0%→20% 10.0%→80%")
 */
function formatProfitTiers(
  tiers: Array<{ profit_percentage: number; volume_percentage_to_sell: number }> | null | undefined
): string {
  if (!tiers || tiers.length === 0) return '-';
  return tiers
    .map(
      (tier) =>
        `${(tier.profit_percentage * 100).toFixed(1)}%→${(tier.volume_percentage_to_sell * 100).toFixed(0)}%`
    )
    .join(' ');
}

/**
 * Get final state display name (localized Korean)
 */
function getFinalStateDisplayName(state: string): string {
  switch (state) {
    case 'Success':
    case 'EvaluatePnL':
      return '완료';
    case 'Failure':
      return '실패';
    case 'Abort':
      return '중단';
    default:
      return state;
  }
}

/**
 * Sortable Header Cell
 */
interface SortableHeaderProps {
  field: CompletedSortField;
  currentSort: SortConfig;
  onSort: (field: CompletedSortField) => void;
  children: React.ReactNode;
  width: string;
  textAlign?: 'left' | 'right' | 'center';
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  currentSort,
  onSort,
  children,
  width,
  textAlign = 'left',
}) => {
  const isActive = currentSort.field === field;

  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        width,
        fontSize: '0.8rem',
        fontWeight: 600,
        textAlign,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          '& .MuiTypography-root': {
            color: theme.accent,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            textAlign === 'right'
              ? 'flex-end'
              : textAlign === 'center'
                ? 'center'
                : 'flex-start',
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: 'inherit',
            color: isActive ? theme.accent : theme.text.primary,
            fontWeight: isActive ? 700 : 600,
          }}
        >
          {children}
        </Typography>
        {isActive &&
          (currentSort.direction === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ))}
      </Box>
    </Box>
  );
};

/**
 * Completed Strategy Row Component
 */
interface CompletedStrategyRowProps {
  strategy: CompletedStrategyListItemV2;
  onViewDetails: (strategyId: string) => void;
  isRepairing?: boolean;
}

const CompletedStrategyRow: React.FC<CompletedStrategyRowProps> = React.memo(({
  strategy,
  onViewDetails,
  isRepairing,
}) => {
  const pnlColor = strategy.pnl_summary.total_pnl_usdt >= 0 ? theme.profit : theme.loss;
  const stateColor = getStateColor(strategy.final_state);

  // Get exchange index for logo
  const spotExchangeIndex = getExchangeIndex(strategy.buy_exchange);

  const handleClick = () => {
    onViewDetails(strategy.strategy_id);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 1,
        borderBottom: `1px solid ${theme.border}`,
        cursor: 'pointer',
        backgroundColor: isRepairing ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
        '&:hover': {
          backgroundColor: isRepairing ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 255, 255, 0.05)',
        },
      }}
    >
      {/* Symbol with Exchange Logo - like PositionRow */}
      <Box
        sx={{
          width: '14%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        {spotExchangeIndex !== undefined && (
          <Avatar
            src={getExchangeLogo(spotExchangeIndex)}
            sx={{ width: 20, height: 20, flexShrink: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              color: theme.text.primary,
              fontFamily: 'monospace',
            }}
          >
            {strategy.base_currency || '-'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.7rem',
              color: stateColor,
              fontWeight: 500,
            }}
          >
            {getFinalStateDisplayName(strategy.final_state)}
          </Typography>
        </Box>
      </Box>

      {/* Date Range */}
      <Box sx={{ width: '22%', textAlign: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: theme.text.secondary,
            display: 'block',
          }}
        >
          {formatDate(strategy.started_at)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: theme.text.primary,
            display: 'block',
          }}
        >
          → {formatDate(strategy.completed_at)}
        </Typography>
      </Box>

      {/* Duration - separate column */}
      <Box sx={{ width: '10%', textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.85rem',
            color: theme.text.primary,
            fontFamily: 'monospace',
          }}
        >
          {formatDuration(strategy.started_at, strategy.completed_at)}
        </Typography>
      </Box>

      {/* Profit Tiers */}
      <Box sx={{ width: '14%', textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.7rem',
            color: theme.text.secondary,
            fontFamily: 'monospace',
          }}
        >
          {formatProfitTiers(strategy.profit_tiers)}
        </Typography>
      </Box>

      {/* P&L */}
      <Box sx={{ width: '40%', textAlign: 'right', pr: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
        {isRepairing && (
          <CircularProgress size={16} sx={{ color: theme.accent }} />
        )}
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              fontFamily: 'monospace',
              color: pnlColor,
            }}
          >
            {formatProfit(strategy.pnl_summary.total_pnl_usdt)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              color: pnlColor,
            }}
          >
            ({formatPercent(strategy.pnl_summary.pnl_percent)})
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

CompletedStrategyRow.displayName = 'CompletedStrategyRow';

/**
 * Table Header Component
 */
interface TableHeaderProps {
  sortConfig: SortConfig;
  onSort: (field: CompletedSortField) => void;
  t: (key: string) => string;
}

const TableHeader: React.FC<TableHeaderProps> = ({ sortConfig, onSort, t }) => (
  <Box
    sx={{
      display: 'flex',
      backgroundColor: theme.bg.primary,
      borderBottom: `2px solid ${theme.border}`,
      py: 1,
      px: 1,
    }}
  >
    <SortableHeader
      field="base"
      currentSort={sortConfig}
      onSort={onSort}
      width="14%"
      textAlign="center"
    >
      {t('traderDashboard.completed.header.coin')}
    </SortableHeader>
    <SortableHeader
      field="completedAt"
      currentSort={sortConfig}
      onSort={onSort}
      width="22%"
      textAlign="center"
    >
      {t('traderDashboard.completed.header.dateRange')}
    </SortableHeader>
    <SortableHeader
      field="duration"
      currentSort={sortConfig}
      onSort={onSort}
      width="10%"
      textAlign="center"
    >
      {t('traderDashboard.completed.header.duration')}
    </SortableHeader>
    <Box
      sx={{
        width: '14%',
        fontSize: '0.8rem',
        fontWeight: 600,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 'inherit',
          color: theme.text.primary,
          fontWeight: 600,
        }}
      >
        {t('traderDashboard.completed.header.profitTiers')}
      </Typography>
    </Box>
    <SortableHeader
      field="pnl"
      currentSort={sortConfig}
      onSort={onSort}
      width="40%"
      textAlign="right"
    >
      {t('traderDashboard.completed.header.pnl')}
    </SortableHeader>
  </Box>
);

/**
 * Completed Strategies Table Component
 */
export const CompletedStrategiesTable: React.FC = () => {
  const { t } = useTranslation();
  const {
    completedStrategies,
    completedCount,
    completedUnavailableCount,
    completedLoading,
    completedError,
    refreshCompletedStrategies,
    repairStrategy,
    traderName,
  } = useTraderData();

  // Sort state - default to completed date descending (most recent first)
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'completedAt',
    direction: 'desc',
  });

  // Search filter state
  const [searchQuery, setSearchQuery] = useState('');

  // Exchange filter state
  const [exchangeFilters, setExchangeFilters] = useState<Record<string, boolean>>({
    binance: true,
    upbit: true,
    bithumb: true,
  });

  // Date range filter (uses completed_at as the reference date)
  type DateRange = 'all' | '7d' | '14d' | '30d' | '90d' | '1y' | 'custom';

  // Custom date range state - persist to localStorage
  const STORAGE_KEY_CUSTOM_DATE_RANGE = 'trader_custom_date_range';

  // Initialize dateRange from localStorage
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CUSTOM_DATE_RANGE);
      if (stored) {
        const { dateRange: storedRange } = JSON.parse(stored);
        if (storedRange) return storedRange as DateRange;
      }
    } catch {
      // Ignore parse errors
    }
    return 'all';
  });

  const [customStartDate, setCustomStartDate] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CUSTOM_DATE_RANGE);
      if (stored) {
        const { startDate } = JSON.parse(stored);
        return startDate || '';
      }
    } catch {
      // Ignore parse errors
    }
    return '';
  });
  const [customEndDate, setCustomEndDate] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CUSTOM_DATE_RANGE);
      if (stored) {
        const { endDate } = JSON.parse(stored);
        return endDate || '';
      }
    } catch {
      // Ignore parse errors
    }
    return '';
  });
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);

  // Persist custom date range and dateRange selection to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY_CUSTOM_DATE_RANGE,
      JSON.stringify({
        dateRange,
        startDate: customStartDate,
        endDate: customEndDate
      })
    );
  }, [dateRange, customStartDate, customEndDate]);

  // Swap start and end dates
  const handleSwapDates = useCallback(() => {
    setCustomStartDate(customEndDate);
    setCustomEndDate(customStartDate);
  }, [customStartDate, customEndDate]);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);

  // State filter (all, completed, aborted)
  type StateFilter = 'all' | 'completed' | 'aborted';
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');

  // Exchange toggle handler
  const handleExchangeToggle = useCallback((exchange: string) => {
    setExchangeFilters((prev) => ({
      ...prev,
      [exchange]: !prev[exchange],
    }));
  }, []);

  // Dialog state for strategy details
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);

  // Repair state
  const [repairing, setRepairing] = useState(false);
  const [repairingStrategyId, setRepairingStrategyId] = useState<string | null>(null);
  const [repairConfirmOpen, setRepairConfirmOpen] = useState(false);

  // Handle opening strategy detail dialog
  const handleViewDetails = useCallback((strategyId: string) => {
    setSelectedStrategyId(strategyId);
    setDetailDialogOpen(true);
  }, []);

  // Handle closing strategy detail dialog
  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedStrategyId(null);
  }, []);

  // Handle sort toggle
  const handleSort = useCallback((field: CompletedSortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // Calculate date range cutoff
  const dateRangeCutoff = useMemo(() => {
    if (dateRange === 'all') return { start: 0, end: Infinity };
    if (dateRange === 'custom') {
      const start = customStartDate ? new Date(customStartDate).getTime() : 0;
      const end = customEndDate ? new Date(customEndDate).getTime() : Infinity;
      return { start, end };
    }
    const now = Date.now();
    const days: Record<string, number> = {
      '7d': 7,
      '14d': 14,
      '30d': 30,
      '90d': 90,
      '1y': 365,
    };
    return { start: now - days[dateRange] * 24 * 60 * 60 * 1000, end: Infinity };
  }, [dateRange, customStartDate, customEndDate]);

  // Filtered and sorted strategies
  const sortedStrategies = useMemo(() => {
    if (!completedStrategies.length) return completedStrategies;

    // Apply all filters
    const filtered = completedStrategies.filter((s) => {
      // Exchange filter (check buy_exchange)
      const exchange = (s.buy_exchange || '').toLowerCase();
      if (!exchangeFilters[exchange]) {
        return false;
      }

      // Date range filter (uses completed_at)
      const completedAt = s.completed_at || 0;
      if (completedAt < dateRangeCutoff.start || completedAt > dateRangeCutoff.end) {
        return false;
      }

      // State filter
      if (stateFilter !== 'all') {
        const finalState = s.final_state || '';
        if (stateFilter === 'completed') {
          // Include Success and EvaluatePnL as completed
          if (finalState !== 'Success' && finalState !== 'EvaluatePnL') {
            return false;
          }
        } else if (stateFilter === 'aborted') {
          if (finalState !== 'Abort') {
            return false;
          }
        }
      }

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const strategyId = (s.strategy_id || '').toLowerCase();
        const base = (s.base_currency || '').toLowerCase();
        const buyExchange = exchange;
        const sellExchange = (s.sell_exchange || '').toLowerCase();
        if (
          !strategyId.includes(query) &&
          !base.includes(query) &&
          !buyExchange.includes(query) &&
          !sellExchange.includes(query)
        ) {
          return false;
        }
      }

      return true;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.field) {
        case 'base':
          comparison = (a.base_currency || '').localeCompare(b.base_currency || '');
          break;
        case 'completedAt':
          comparison = (a.completed_at || 0) - (b.completed_at || 0);
          break;
        case 'duration': {
          // Duration = completed_at - started_at
          const aDuration = (a.completed_at || 0) - (a.started_at || 0);
          const bDuration = (b.completed_at || 0) - (b.started_at || 0);
          comparison = aDuration - bDuration;
          break;
        }
        case 'pnl': {
          // Handle potential null/undefined pnl_summary
          const aPnl = a.pnl_summary?.total_pnl_usdt ?? 0;
          const bPnl = b.pnl_summary?.total_pnl_usdt ?? 0;
          comparison = aPnl - bPnl;
          break;
        }
        case 'pnlPercent': {
          const aPercent = a.pnl_summary?.pnl_percent ?? 0;
          const bPercent = b.pnl_summary?.pnl_percent ?? 0;
          comparison = aPercent - bPercent;
          break;
        }
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [completedStrategies, sortConfig, searchQuery, exchangeFilters, dateRangeCutoff, stateFilter]);

  const totalPnl = useMemo(
    () => sortedStrategies.reduce((sum, s) => sum + s.pnl_summary.total_pnl_usdt, 0),
    [sortedStrategies],
  );

  // Calculate 24-hour P&L from filtered strategies
  const pnl24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return sortedStrategies
      .filter((s) => (s.completed_at || 0) >= cutoff)
      .reduce((sum, s) => sum + s.pnl_summary.total_pnl_usdt, 0);
  }, [sortedStrategies]);

  // Handle repair button click - show confirmation dialog
  const handleRepairClick = useCallback(() => {
    setRepairConfirmOpen(true);
  }, []);

  // Handle repair confirmation
  const handleRepairConfirm = useCallback(async () => {
    setRepairConfirmOpen(false);
    if (repairing || sortedStrategies.length === 0) return;

    setRepairing(true);
    try {
      // Repair strategies sequentially with 10 second delay to avoid overloading the server
      for (let i = 0; i < sortedStrategies.length; i++) {
        const strategyId = sortedStrategies[i].strategy_id;
        setRepairingStrategyId(strategyId);
        await repairStrategy(strategyId);
        // Add 10 second delay between repairs (except after the last one)
        if (i < sortedStrategies.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10000));
        }
      }
      // Refresh after all repairs
      await refreshCompletedStrategies();
    } finally {
      setRepairing(false);
      setRepairingStrategyId(null);
    }
  }, [repairing, sortedStrategies, repairStrategy, refreshCompletedStrategies]);

  // Handle repair cancel
  const handleRepairCancel = useCallback(() => {
    setRepairConfirmOpen(false);
  }, []);

  // Export handlers
  const handleExportClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleExportCsv = useCallback(() => {
    exportCompletedStrategies(sortedStrategies, 'csv');
    setExportMenuAnchor(null);
  }, [sortedStrategies]);

  const handleExportXlsx = useCallback(() => {
    exportCompletedStrategies(sortedStrategies, 'xlsx');
    setExportMenuAnchor(null);
  }, [sortedStrategies]);

  // Custom date range handlers
  const handleDatePickerOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(event.currentTarget);
  }, []);

  const handleDatePickerClose = useCallback(() => {
    setDatePickerAnchor(null);
  }, []);

  const handleApplyCustomDateRange = useCallback(() => {
    setDateRange('custom');
    setDatePickerAnchor(null);
  }, []);

  const handleDateRangeChange = useCallback((value: string) => {
    if (value === 'custom') {
      // Don't set dateRange to custom yet, wait for user to select dates
      setDatePickerAnchor(document.querySelector('[data-date-range-select]') as HTMLElement);
    } else {
      setDateRange(value as DateRange);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header with refresh button and total P&L */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: theme.text.primary,
            fontWeight: 600,
          }}
        >
          {t('traderDashboard.completed.title', { count: completedCount })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: pnl24h >= 0 ? theme.profit : theme.loss,
              fontWeight: 600,
            }}
          >
            24h: {formatProfit(pnl24h)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: totalPnl >= 0 ? theme.profit : theme.loss,
              fontWeight: 600,
            }}
          >
            {t('traderDashboard.completed.totalPnl')}: {formatProfit(totalPnl)}
          </Typography>
          <Tooltip title={t('traderDashboard.completed.export.tooltip', { count: sortedStrategies.length })} arrow>
            <span>
              <IconButton
                onClick={handleExportClick}
                disabled={sortedStrategies.length === 0}
                size="small"
                sx={{ color: theme.text.secondary }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportClose}
            PaperProps={{
              sx: {
                backgroundColor: theme.bg.secondary,
                border: `1px solid ${theme.border}`,
              },
            }}
          >
            <MenuItem onClick={handleExportCsv} sx={{ color: theme.text.primary, fontSize: '0.85rem' }}>
              {t('traderDashboard.completed.export.csv')}
            </MenuItem>
            <MenuItem onClick={handleExportXlsx} sx={{ color: theme.text.primary, fontSize: '0.85rem' }}>
              {t('traderDashboard.completed.export.xlsx')}
            </MenuItem>
          </Menu>
          <Tooltip title={t('traderDashboard.completed.repairAllTooltip', { count: sortedStrategies.length })} arrow>
            <span>
              <IconButton
                onClick={handleRepairClick}
                disabled={repairing || sortedStrategies.length === 0}
                size="small"
                sx={{ color: theme.text.secondary }}
              >
                {repairing ? (
                  <CircularProgress size={16} sx={{ color: theme.accent }} />
                ) : (
                  <BuildIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={refreshCompletedStrategies}
            disabled={completedLoading}
            size="small"
            sx={{ color: theme.text.secondary }}
          >
            {completedLoading ? (
              <CircularProgress size={16} sx={{ color: theme.accent }} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Error message */}
      {completedError && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: theme.loss }}>
            {completedError}
          </Typography>
        </Box>
      )}

      {completedUnavailableCount > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(210, 153, 34, 0.1)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: theme.warning }}>
            {t('traderDashboard.completed.partialUnavailable', {
              count: completedUnavailableCount,
            })}
          </Typography>
        </Box>
      )}

      {/* Loading state */}
      {completedLoading && completedStrategies.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: theme.accent }} />
        </Box>
      )}

      {/* Empty state */}
      {!completedLoading && completedStrategies.length === 0 && !completedError && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: theme.text.secondary,
          }}
        >
          <HistoryIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6">{t('traderDashboard.completed.empty.title')}</Typography>
          <Typography variant="body2">{t('traderDashboard.completed.empty.subtitle')}</Typography>
        </Box>
      )}

      {/* Table */}
      {completedStrategies.length > 0 && (
        <>
          {/* Filter Bar */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterListIcon sx={{ color: theme.text.secondary, fontSize: 18 }} />

              {/* Exchange filter toggles with logos */}
              <Tooltip title="Binance" arrow>
                <IconButton
                  onClick={() => handleExchangeToggle('binance')}
                  size="small"
                  sx={{
                    p: 0.5,
                    opacity: exchangeFilters.binance ? 1 : 0.3,
                    border: `1px solid ${exchangeFilters.binance ? '#f3ba2f' : theme.border}`,
                    '&:hover': { opacity: 1, backgroundColor: 'rgba(243, 186, 47, 0.1)' },
                  }}
                >
                  <Avatar src={getExchangeLogo(3)} sx={{ width: 18, height: 18 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Upbit" arrow>
                <IconButton
                  onClick={() => handleExchangeToggle('upbit')}
                  size="small"
                  sx={{
                    p: 0.5,
                    opacity: exchangeFilters.upbit ? 1 : 0.3,
                    border: `1px solid ${exchangeFilters.upbit ? '#005aff' : theme.border}`,
                    '&:hover': { opacity: 1, backgroundColor: 'rgba(0, 90, 255, 0.1)' },
                  }}
                >
                  <Avatar src={getExchangeLogo(0)} sx={{ width: 18, height: 18 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Bithumb" arrow>
                <IconButton
                  onClick={() => handleExchangeToggle('bithumb')}
                  size="small"
                  sx={{
                    p: 0.5,
                    opacity: exchangeFilters.bithumb ? 1 : 0.3,
                    border: `1px solid ${exchangeFilters.bithumb ? '#ff7a00' : theme.border}`,
                    '&:hover': { opacity: 1, backgroundColor: 'rgba(255, 122, 0, 0.1)' },
                  }}
                >
                  <Avatar src={getExchangeLogo(1)} sx={{ width: 18, height: 18 }} />
                </IconButton>
              </Tooltip>

              {/* Date range filter */}
              <FormControl size="small" sx={{ minWidth: 80, ml: 1 }}>
                <Select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  data-date-range-select
                  sx={{
                    fontSize: '0.75rem',
                    color: theme.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.border,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.accent,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.accent,
                    },
                    '& .MuiSelect-select': {
                      py: 0.5,
                    },
                  }}
                >
                  <MenuItem value="all">{t('traderDashboard.completed.filter.all')}</MenuItem>
                  <MenuItem value="7d">{t('traderDashboard.completed.filter.7d')}</MenuItem>
                  <MenuItem value="14d">{t('traderDashboard.completed.filter.14d')}</MenuItem>
                  <MenuItem value="30d">{t('traderDashboard.completed.filter.30d')}</MenuItem>
                  <MenuItem value="90d">{t('traderDashboard.completed.filter.90d')}</MenuItem>
                  <MenuItem value="1y">{t('traderDashboard.completed.filter.1y')}</MenuItem>
                  <MenuItem value="custom">{t('traderDashboard.completed.filter.custom')}</MenuItem>
                </Select>
              </FormControl>

              {/* Custom date range indicator */}
              {dateRange === 'custom' && (
                <Tooltip title={t('traderDashboard.completed.filter.editCustomRange')} arrow>
                  <IconButton
                    size="small"
                    onClick={handleDatePickerOpen}
                    sx={{
                      p: 0.5,
                      color: theme.accent,
                      '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}

              {/* Date picker popover */}
              <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: theme.bg.secondary,
                    border: `1px solid ${theme.border}`,
                    p: 2,
                    minWidth: 280,
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.text.primary, fontWeight: 600, mb: 2 }}
                >
                  {t('traderDashboard.completed.filter.customRangeTitle')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label={t('traderDashboard.completed.filter.startDate')}
                    type="datetime-local"
                    size="small"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.bg.primary,
                        '& fieldset': { borderColor: theme.border },
                        '&:hover fieldset': { borderColor: theme.accent },
                        '&.Mui-focused fieldset': { borderColor: theme.accent },
                      },
                      '& .MuiOutlinedInput-input': { color: theme.text.primary, fontSize: '0.85rem' },
                      '& .MuiInputLabel-root': { color: theme.text.secondary },
                    }}
                  />
                  {/* Swap button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={t('traderDashboard.completed.filter.swapDates')}>
                      <IconButton
                        size="small"
                        onClick={handleSwapDates}
                        disabled={!customStartDate && !customEndDate}
                        sx={{
                          color: theme.text.secondary,
                          '&:hover': { color: theme.accent },
                        }}
                      >
                        <SwapHorizIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    label={t('traderDashboard.completed.filter.endDate')}
                    type="datetime-local"
                    size="small"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.bg.primary,
                        '& fieldset': { borderColor: theme.border },
                        '&:hover fieldset': { borderColor: theme.accent },
                        '&.Mui-focused fieldset': { borderColor: theme.accent },
                      },
                      '& .MuiOutlinedInput-input': { color: theme.text.primary, fontSize: '0.85rem' },
                      '& .MuiInputLabel-root': { color: theme.text.secondary },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      onClick={handleDatePickerClose}
                      sx={{ color: theme.text.secondary }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleApplyCustomDateRange}
                      disabled={!customStartDate && !customEndDate}
                      sx={{
                        backgroundColor: theme.accent,
                        '&:hover': { backgroundColor: theme.accent, opacity: 0.9 },
                      }}
                    >
                      {t('traderDashboard.completed.filter.apply')}
                    </Button>
                  </Box>
                </Box>
              </Popover>

              {/* State filter */}
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value as StateFilter)}
                  sx={{
                    fontSize: '0.75rem',
                    color: theme.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.border,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.accent,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.accent,
                    },
                    '& .MuiSelect-select': {
                      py: 0.5,
                    },
                  }}
                >
                  <MenuItem value="all">{t('traderDashboard.completed.stateFilter.all')}</MenuItem>
                  <MenuItem value="completed">{t('traderDashboard.completed.stateFilter.completed')}</MenuItem>
                  <MenuItem value="aborted">{t('traderDashboard.completed.stateFilter.aborted')}</MenuItem>
                </Select>
              </FormControl>

              {/* Count indicator */}
              <Typography variant="caption" sx={{ color: theme.text.muted, ml: 1 }}>
                {sortedStrategies.length}/{completedStrategies.length}
              </Typography>
            </Stack>

            <TextField
              size="small"
              placeholder={t('traderDashboard.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.text.secondary, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.bg.secondary,
                  '& fieldset': {
                    borderColor: theme.border,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.accent,
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: theme.text.primary,
                  fontSize: '0.85rem',
                  py: 0.75,
                  '&::placeholder': {
                    color: theme.text.secondary,
                    opacity: 1,
                  },
                },
              }}
            />
          </Stack>

          <Box
            sx={{
              backgroundColor: theme.bg.secondary,
              border: `1px solid ${theme.border}`,
              borderRadius: 1,
              overflow: 'hidden',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {/* Header */}
            <TableHeader sortConfig={sortConfig} onSort={handleSort} t={t} />

            {/* Rows */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {sortedStrategies.map((strategy) => (
                  <CompletedStrategyRow
                    key={strategy.strategy_id}
                    strategy={strategy}
                    onViewDetails={handleViewDetails}
                    isRepairing={repairingStrategyId === strategy.strategy_id}
                  />
                ))}
            </Box>
          </Box>
        </>
      )}

      {/* Repair Confirmation Dialog */}
      <Dialog
        open={repairConfirmOpen}
        onClose={handleRepairCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: theme.text.primary, fontWeight: 600 }}>
          {t('traderDashboard.completed.repairConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.text.primary, mb: 2 }}>
            {t('traderDashboard.completed.repairConfirm.message', { count: sortedStrategies.length })}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.text.secondary, fontSize: '0.85rem' }}>
            ⚠️ {t('traderDashboard.completed.repairConfirm.warning1')}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.text.secondary, fontSize: '0.85rem', mt: 1 }}>
            ⚠️ {t('traderDashboard.completed.repairConfirm.warning2')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.accent,
              fontSize: '0.85rem',
              mt: 2,
              fontStyle: 'italic',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              p: 1,
              borderRadius: 1,
            }}
          >
            💡 {t('traderDashboard.completed.repairConfirm.tip')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleRepairCancel}
            sx={{
              color: theme.text.secondary,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {t('traderDashboard.completed.repairConfirm.cancel')}
          </Button>
          <Button
            onClick={handleRepairConfirm}
            variant="contained"
            sx={{
              backgroundColor: theme.accent,
              color: theme.text.primary,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.accent,
                opacity: 0.9,
              },
            }}
          >
            {t('traderDashboard.completed.repairConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Strategy Detail Dialog */}
      {traderName && selectedStrategyId && (
        <StrategyDetailDialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          strategyId={selectedStrategyId}
          traderName={traderName}
        />
      )}
    </Box>
  );
};

export default CompletedStrategiesTable;
